// =====================================================
// TREATMENT SELECTOR - Selector de tratamiento
// El tratamiento da nombre al presupuesto
// FIX: usa getForSelectBySite(siteId) para mostrar solo
// los tratamientos vinculados al centro activo.
// =====================================================
import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, X, Tag, Loader2 } from 'lucide-react';
import { treatmentsApi, type TreatmentSelect } from '../../api/treatments';

interface TreatmentSelectorProps {
  value: TreatmentSelect | null;
  onChange: (treatment: TreatmentSelect | null) => void;
  /** ID del centro activo. Si no se proporciona carga todos (fallback). */
  siteId?: string | null;
  placeholder?: string;
  className?: string;
}

export function TreatmentSelector({
  value,
  onChange,
  siteId,
  placeholder = 'Seleccionar tratamiento...',
  className = '',
}: TreatmentSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [treatments, setTreatments] = useState<TreatmentSelect[]>([]);
  const [filteredTreatments, setFilteredTreatments] = useState<TreatmentSelect[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Recargar cuando cambia el centro
  useEffect(() => {
    loadTreatments();
  }, [siteId]);

  // Filtrar por búsqueda
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredTreatments(treatments);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredTreatments(
        treatments.filter(
          (t) =>
            t.name.toLowerCase().includes(query) ||
            t.code.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, treatments]);

  // Cerrar al clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadTreatments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // FIX: si hay siteId, filtrar por centro; si no, cargar todos como fallback
      const data = siteId
        ? await treatmentsApi.getForSelectBySite(siteId)
        : await treatmentsApi.getForSelect();
      setTreatments(data);
      setFilteredTreatments(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar los tratamientos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (treatment: TreatmentSelect) => {
    onChange(treatment);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSearchQuery('');
  };

  const handleOpen = () => {
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {/* Campo de selección */}
      <div
        onClick={handleOpen}
        className={`
          flex items-center gap-2 w-full px-4 py-2.5 bg-white border rounded-xl cursor-pointer
          transition-all
          ${isOpen ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-300 hover:border-gray-400'}
        `}
      >
        <Tag className="h-4 w-4 text-gray-400 flex-shrink-0" />

        {value ? (
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <span className="text-xs font-mono text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              {value.code}
            </span>
            <span className="font-medium text-gray-900 truncate">{value.name}</span>
          </div>
        ) : (
          <span className="flex-1 text-gray-500">{placeholder}</span>
        )}

        {value ? (
          <button
            onClick={handleClear}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        ) : (
          <ChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          {/* Buscador */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar tratamiento..."
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>

          {/* Lista */}
          <div className="max-h-60 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : error ? (
              <div className="p-4 text-sm text-red-600">{error}</div>
            ) : filteredTreatments.length > 0 ? (
              <div className="py-1">
                {filteredTreatments.map((treatment) => (
                  <button
                    key={treatment.id}
                    onClick={() => handleSelect(treatment)}
                    className={`
                      w-full flex items-center gap-2 px-4 py-2.5 text-left transition-colors
                      ${value?.id === treatment.id ? 'bg-blue-50' : 'hover:bg-gray-50'}
                    `}
                  >
                    <span className="text-xs font-mono text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                      {treatment.code}
                    </span>
                    <span
                      className={`flex-1 ${
                        value?.id === treatment.id
                          ? 'font-medium text-blue-700'
                          : 'text-gray-900'
                      }`}
                    >
                      {treatment.name}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-sm text-gray-500">
                No se encontraron tratamientos
              </div>
            )}
          </div>

          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              El tratamiento define el nombre del presupuesto
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
